/**
 * @file: useDeliveryCheckLayout.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { RefObject, useCallback, useRef } from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollViewProps,
} from 'react-native';
import { FlatList, ScrollView } from 'react-native-gesture-handler';
import { useSharedValue } from 'react-native-reanimated';
import type { DeliveryCheckCellRef } from '../components/DeliveryCheckCell';

export const useDeliveryCheckScrollView = (options: {
  minPos: number;
  maxPos: number;
  horizontal?: boolean;
}) => {
  const { minPos, maxPos, horizontal } = options;

  const scrollOffset = useSharedValue(0);
  const scrollViewRef = useRef<ScrollView>(null);

  const cellLayoutMap = useRef(new Map<string, LayoutRectangle>()).current;
  const cellRefMap = useRef(
    new Map<string, DeliveryCheckCellRef | null>(),
  ).current;

  const onCellLayout = useCallback(
    (key: string, e: LayoutChangeEvent) => {
      cellLayoutMap.set(key, e.nativeEvent.layout);
    },
    [cellLayoutMap],
  );

  const scrollToItemAnimated = usePersistFn(
    (item: { key: string; index: number }, callback?: () => void) => {
      const { key, index } = item;
      const layout = cellLayoutMap.get(key);
      if (layout != null) {
        const pos = horizontal ? layout.x : layout.y;
        const length = horizontal ? layout.width : layout.height;
        const startPos = pos - scrollOffset.value;
        const endPos = startPos + length;

        let duration = 0;

        if (startPos < minPos || endPos > maxPos) {
          const offset = pos - (maxPos - minPos - length) / 2;
          const flatListRef = scrollViewRef as unknown as RefObject<FlatList>;
          if (flatListRef.current?.scrollToIndex) {
            flatListRef.current?.scrollToIndex({
              index,
              viewOffset: (maxPos - minPos - length) / 2,
              animated: true,
            });
          } else if (horizontal) {
            scrollViewRef.current?.scrollTo?.({
              x: offset,
              animated: true,
            });
          } else {
            scrollViewRef.current?.scrollTo?.({
              y: offset,
              animated: true,
            });
          }

          duration = 350;
        }

        delayed(() => {
          cellRefMap.get(key)?.shake(callback);
        }, duration);
      }
    },
  );

  const onCellRef = useCallback(
    (key: string, ref: DeliveryCheckCellRef | null) => {
      cellRefMap.set(key, ref);
    },
    [cellRefMap],
  );

  const onScroll: ScrollViewProps['onScroll'] = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      'worklet';
      scrollOffset.value = horizontal
        ? e.nativeEvent.contentOffset.x
        : e.nativeEvent.contentOffset.y;
    },
    [scrollOffset, horizontal],
  );

  return {
    scrollOffset,
    scrollViewRef,
    cellRefMap,
    cellLayoutMap,
    scrollToItemAnimated,
    onCellLayout,
    onCellRef,
    onScroll,
  };
};
